/**
 * The AI chat widget a merchant embeds on their own website. It is the second
 * surface their Talab assistant lives on — the first being their Talab shop
 * page — so it carries the same brand controls and the same Scale-plan gate on
 * removing Talab branding.
 */
export type WidgetTheme = 'light' | 'dark'
export type WidgetCorner = 'rounded' | 'soft' | 'square'
export type WidgetPosition = 'bottom-right' | 'bottom-left'
export type WidgetSize = 'compact' | 'standard' | 'expanded' | 'full'
export type WidgetLauncher = 'bubble' | 'pill' | 'bar'

export interface WidgetConfig {
  /** Identity */
  name: string
  subtitle: string
  avatar: string
  /** Appearance */
  primaryColor: string
  accentColor: string
  theme: WidgetTheme
  corner: WidgetCorner
  position: WidgetPosition
  size: WidgetSize
  launcher: WidgetLauncher
  launcherLabel: string
  offsetX: number
  offsetY: number
  /** Content */
  greeting: string
  prompts: string[]
  placeholder: string
  /** Behaviour */
  autoOpen: boolean
  autoOpenDelay: number
  showOnMobile: boolean
  captureLeads: boolean
  leadPrompt: string
  showProducts: boolean
  /** Branding — removing it requires the Scale plan */
  showTalabBranding: boolean
  /** Install */
  domains: string[]
}

/** The widget is an edge drawer, so size means how wide the panel slides in. */
export const WIDGET_SIZE: Record<
  WidgetSize,
  {
    w: number
    label: string
  }
> = {
  compact: {
    w: 360,
    label: 'Narrow',
  },
  standard: {
    w: 420,
    label: 'Standard',
  },
  expanded: {
    w: 500,
    label: 'Wide',
  },
  /** Covers the whole page — an immersive, app-like conversation. */
  full: {
    w: 0,
    label: 'Full screen',
  },
}

export const WIDGET_RADIUS: Record<WidgetCorner, number> = {
  rounded: 24,
  soft: 14,
  square: 4,
}

/** Seeded with EasyLease so the builder obviously produces a branded widget. */
export const easyleaseWidget: WidgetConfig = {
  name: 'EasyLease Assistant',
  subtitle: 'Vehicle rental · Replies instantly',
  avatar:
    'https://cdn.magicpatterns.com/uploads/4dHNpFvrhGx8T4VSYNGooH/Easylease_logo.png',
  primaryColor: '#4C7FC1',
  accentColor: '#F5CE3E',
  theme: 'light',
  corner: 'soft',
  position: 'bottom-right',
  size: 'standard',
  launcher: 'pill',
  launcherLabel: 'Ask EasyLease',
  offsetX: 24,
  offsetY: 24,
  greeting:
    'Hi! I can help you find a vehicle, check availability or quote a monthly rate. What are you looking for?',
  prompts: [
    'Show me available SUVs',
    'What are your monthly rates?',
    'I need airport pickup',
  ],
  placeholder: 'Ask EasyLease anything…',
  autoOpen: true,
  autoOpenDelay: 8,
  showOnMobile: true,
  captureLeads: true,
  leadPrompt: 'Where should we send your quote?',
  showProducts: true,
  showTalabBranding: true,
  domains: ['easylease.ae', 'www.easylease.ae'],
}

/** The embed snippet a merchant pastes into their own site. */
export function widgetSnippet(widget: WidgetConfig, handle: string): string {
  return `<!-- Talab AI Assistant -->
<script
  src="https://cdn.talab.app/widget.js"
  data-shop="${handle}"
  data-key="pk_live_8f2a41c7d93b"
  data-position="${widget.position}"
  data-theme="${widget.theme}"
  defer
></script>`
}
